import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
	getAdminProducts,
	getAdminProductById,
	deleteProductById,
	updateProductById,
	getCategories,
	createProductVariant,
	updateProductVariant,
	deleteProductVariant,
	addProductImages,
	addVariantImages,
	deleteProductImage,
} from "../../services/adminService";

// ── Status badge ──────────────────────────────────────────────────
const StatusBadge = ({ available, stock }) => {
	if (!available) {
		return (
			<span style={badge("red")}>
				<Dot color="#A32D2D" /> Inactif
			</span>
		);
	}

	if (stock === 0) {
		return (
			<span style={badge("amber")}>
				<Dot color="#854F0B" /> Épuisé
			</span>
		);
	}

	return (
		<span style={badge("green")}>
			<Dot color="#3B6D11" /> Actif
		</span>
	);
};

const Dot = ({ color }) => (
	<span
		style={{
			display: "inline-block",
			width: 6,
			height: 6,
			borderRadius: "50%",
			background: color,
			marginRight: 5,
			flexShrink: 0,
		}}
	/>
);

const badge = (color) => {
	const map = {
		green: {
			bg: "#EAF3DE",
			text: "#3B6D11",
		},
		amber: {
			bg: "#FAEEDA",
			text: "#854F0B",
		},
		red: {
			bg: "#FCEBEB",
			text: "#A32D2D",
		},
	};

	return {
		display: "inline-flex",
		alignItems: "center",
		padding: "3px 10px",
		borderRadius: 20,
		fontSize: 12,
		fontWeight: 500,
		background: map[color].bg,
		color: map[color].text,
		whiteSpace: "nowrap",
	};
};

// ── Image thumbnail ───────────────────────────────────────────────
const ProductThumb = ({ src, alt }) => (
	<div
		style={{
			width: 80,
			height: 80,
			borderRadius: 14,
			overflow: "hidden",
			background: "#F5F3EE",
			border: "1px solid #E6E1D6",
			flexShrink: 0,
		}}
	>
		{src ? (
			<img
				src={src}
				alt={alt}
				style={{
					width: "100%",
					height: "100%",
					objectFit: "cover",
					display: "block",
				}}
			/>
		) : (
			<div
				style={{
					width: "100%",
					height: "100%",
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					color: "#B4B2A9",
					fontSize: 26,
				}}
			>
				☕
			</div>
		)}
	</div>
);

// ── Form field ────────────────────────────────────────────────────
const Field = ({ label, children, required }) => (
	<div
		style={{
			display: "flex",
			flexDirection: "column",
			gap: 4,
		}}
	>
		<label
			style={{
				fontSize: 12,
				fontWeight: 500,
				color: "#6B6B6B",
				letterSpacing: "0.04em",
			}}
		>
			{label}
			{required && (
				<span
					style={{
						color: "#E24B4A",
						marginLeft: 2,
					}}
				>
					*
				</span>
			)}
		</label>

		{children}
	</div>
);

const inputStyle = {
	border: "0.5px solid #D3D1C7",
	borderRadius: 10,
	padding: "9px 12px",
	fontSize: 14,
	color: "#2A2A2A",
	background: "#FFFCF8",
	outline: "none",
	width: "100%",
	boxSizing: "border-box",
	fontFamily: "inherit",
};

// ── Edit modal ────────────────────────────────────────────────────
const ProductEditModal = ({
	form,
	onChange,
	onSubmit,
	onClose,
	submitting,
	categories,
	newProductImages,
	newVariantImages,
	onProductImagesChange,
	onVariantImagesChange,
	changeVariant,
	addVariant,
	removeVariant,
	removeExistingImage,
}) => (
	<div
		style={{
			position: "fixed",
			inset: 0,
			zIndex: 50,
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
			background: "rgba(0,0,0,0.45)",
			backdropFilter: "blur(3px)",
			padding: "24px 16px",
		}}
		onClick={onClose}
	>
		<div
			style={{
				width: "100%",
				maxWidth: 900,
				maxHeight: "90vh",
				overflowY: "auto",
				background: "#FFFCF8",
				borderRadius: 20,
				border: "0.5px solid #E6E1D6",
				boxShadow: "0 24px 64px rgba(0,0,0,0.16)",
			}}
			onClick={(e) => e.stopPropagation()}
		>
			{/* Header */}
			<div
				style={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					padding: "18px 24px",
					borderBottom: "0.5px solid #E6E1D6",
					position: "sticky",
					top: 0,
					background: "#FFFCF8",
					zIndex: 10,
					borderRadius: "20px 20px 0 0",
				}}
			>
				<div>
					<h2
						style={{
							margin: 0,
							fontSize: 18,
							fontWeight: 600,
							color: "#2A2A2A",
						}}
					>
						Modifier le produit
					</h2>

					<p
						style={{
							margin: "2px 0 0",
							fontSize: 13,
							color: "#8A857D",
						}}
					>
						Produit, images et variantes
					</p>
				</div>

				<button
					type="button"
					onClick={onClose}
					disabled={submitting}
					style={{
						width: 36,
						height: 36,
						borderRadius: "50%",
						border: "0.5px solid #E6E1D6",
						background: "transparent",
						color: "#6B6B6B",
						cursor: "pointer",
						fontSize: 18,
					}}
				>
					✕
				</button>
			</div>

			<form
				onSubmit={onSubmit}
				style={{
					padding: 24,
					display: "flex",
					flexDirection: "column",
					gap: 24,
				}}
			>
				{/* Informations principales */}
				<section
					style={{
						display: "flex",
						flexDirection: "column",
						gap: 16,
					}}
				>
					<h3
						style={{
							margin: 0,
							fontSize: 16,
							color: "#2A2A2A",
						}}
					>
						Informations
					</h3>

					<div
						style={{
							display: "grid",
							gridTemplateColumns: "1fr 1fr",
							gap: 12,
						}}
					>
						<Field label="Nom du produit" required>
							<input
								name="name"
								value={form.name}
								onChange={onChange}
								style={inputStyle}
								required
							/>
						</Field>

						<Field label="Slug (URL)">
							<input
								name="slug"
								value={form.slug}
								onChange={onChange}
								style={{
									...inputStyle,
									fontFamily: "monospace",
								}}
							/>
						</Field>
					</div>

					<Field label="Catégorie" required>
						<select
							name="category_id"
							value={form.category_id}
							onChange={onChange}
							style={inputStyle}
							required
						>
							<option value="">Sélectionner une catégorie</option>

							{categories.map((category) => (
								<option key={category.id} value={category.id}>
									{category.name}
								</option>
							))}
						</select>
					</Field>

					<Field label="Description courte">
						<input
							name="short_description"
							value={form.short_description || ""}
							onChange={onChange}
							style={inputStyle}
						/>
					</Field>

					<Field label="Description complète">
						<textarea
							name="description"
							value={form.description || ""}
							onChange={onChange}
							rows={4}
							style={{
								...inputStyle,
								resize: "vertical",
								lineHeight: 1.6,
							}}
						/>
					</Field>

					<div
						style={{
							display: "grid",
							gridTemplateColumns: "1fr 1fr",
							gap: 12,
						}}
					>
						<Field label="Prix (CHF)" required>
							<input
								name="price"
								type="number"
								step="0.05"
								min="0"
								value={form.price}
								onChange={onChange}
								style={inputStyle}
								required
							/>
						</Field>

						<Field label="Stock">
							<input
								name="stock"
								type="number"
								min="0"
								value={form.stock}
								onChange={onChange}
								style={inputStyle}
							/>
						</Field>
					</div>

					<div
						style={{
							display: "flex",
							gap: 24,
							padding: "12px 16px",
							background: "#F5F3EE",
							borderRadius: 10,
						}}
					>
						<label
							style={{
								display: "flex",
								alignItems: "center",
								gap: 8,
								fontSize: 14,
							}}
						>
							<input
								type="checkbox"
								name="available"
								checked={form.available}
								onChange={onChange}
							/>
							Produit actif
						</label>

						<label
							style={{
								display: "flex",
								alignItems: "center",
								gap: 8,
								fontSize: 14,
							}}
						>
							<input
								type="checkbox"
								name="featured"
								checked={form.featured}
								onChange={onChange}
							/>
							Mise en avant
						</label>
					</div>
				</section>

				{/* Images générales */}
				<section
					style={{
						borderTop: "1px solid #E6E1D6",
						paddingTop: 20,
					}}
				>
					<h3
						style={{
							margin: "0 0 6px",
							fontSize: 16,
							color: "#2A2A2A",
						}}
					>
						Images du produit
					</h3>

					<p
						style={{
							margin: "0 0 14px",
							fontSize: 12,
							color: "#8A857D",
						}}
					>
						Images générales utilisées lorsque aucune variante spécifique n'est
						sélectionnée.
					</p>

					{form.images?.length > 0 && (
						<div
							style={{
								display: "flex",
								flexWrap: "wrap",
								gap: 10,
								marginBottom: 14,
							}}
						>
							{form.images.map((image) => (
								<div
									key={image.id}
									style={{
										position: "relative",
									}}
								>
									<img
										src={image.image_url}
										alt=""
										style={{
											width: 100,
											height: 100,
											objectFit: "cover",
											borderRadius: 10,
											border: "1px solid #E6E1D6",
										}}
									/>

									<button
										type="button"
										onClick={() => removeExistingImage(image.id)}
										style={{
											position: "absolute",
											top: 5,
											right: 5,
											width: 24,
											height: 24,
											border: "none",
											borderRadius: "50%",
											background: "rgba(0,0,0,0.7)",
											color: "#fff",
											cursor: "pointer",
										}}
									>
										×
									</button>
								</div>
							))}
						</div>
					)}

					{newProductImages.length > 0 && (
						<div
							style={{
								padding: "10px 12px",
								marginBottom: 12,
								borderRadius: 8,
								background: "#EAF3DE",
								fontSize: 12,
								color: "#3B6D11",
							}}
						>
							{newProductImages.length} nouvelle
							{newProductImages.length > 1 ? "s" : ""} image
							{newProductImages.length > 1 ? "s" : ""} à envoyer lors de
							l'enregistrement.
						</div>
					)}

					<input
						type="file"
						accept="image/*"
						multiple
						onChange={onProductImagesChange}
					/>
				</section>

				{/* Variantes */}
				<section
					style={{
						borderTop: "1px solid #E6E1D6",
						paddingTop: 20,
					}}
				>
					<div
						style={{
							display: "flex",
							alignItems: "center",
							justifyContent: "space-between",
							gap: 12,
							marginBottom: 16,
						}}
					>
						<div>
							<h3
								style={{
									margin: 0,
									fontSize: 16,
									color: "#2A2A2A",
								}}
							>
								Variantes
							</h3>

							<p
								style={{
									margin: "4px 0 0",
									fontSize: 12,
									color: "#8A857D",
								}}
							>
								{form.variants?.length || 0} variante
								{form.variants?.length !== 1 ? "s" : ""}
							</p>
						</div>

						<button
							type="button"
							onClick={addVariant}
							style={{
								padding: "8px 14px",
								borderRadius: 9,
								border: "none",
								background: "#2A2A2A",
								color: "#fff",
								cursor: "pointer",
								fontWeight: 600,
							}}
						>
							+ Ajouter une variante
						</button>
					</div>

					{form.variants?.length === 0 ? (
						<div
							style={{
								padding: 20,
								borderRadius: 10,
								background: "#F5F3EE",
								color: "#8A857D",
								textAlign: "center",
								fontSize: 13,
							}}
						>
							Ce produit ne possède aucune variante.
						</div>
					) : (
						<div
							style={{
								display: "flex",
								flexDirection: "column",
								gap: 14,
							}}
						>
							{form.variants.map((variant, index) => {
								const variantKey = variant.id || variant._tempId;

								const pendingImages = newVariantImages[variantKey] || [];

								return (
									<div
										key={variantKey}
										style={{
											padding: 16,
											border: "1px solid #E6E1D6",
											borderRadius: 12,
											background: "#FAF8F4",
										}}
									>
										<div
											style={{
												display: "flex",
												justifyContent: "space-between",
												alignItems: "center",
												gap: 12,
												marginBottom: 14,
											}}
										>
											<strong
												style={{
													fontSize: 14,
													color: "#2A2A2A",
												}}
											>
												Variante {index + 1}
											</strong>

											<button
												type="button"
												onClick={() => removeVariant(index)}
												style={{
													border: "1px solid #F7C1C1",
													background: "transparent",
													color: "#A32D2D",
													borderRadius: 8,
													padding: "6px 10px",
													cursor: "pointer",
												}}
											>
												Supprimer
											</button>
										</div>

										<div
											style={{
												display: "grid",
												gridTemplateColumns: "1fr 1fr",
												gap: 12,
											}}
										>
											<Field label="Nom" required>
												<input
													value={variant.name || ""}
													onChange={(e) =>
														changeVariant(index, "name", e.target.value)
													}
													style={inputStyle}
													required
												/>
											</Field>

											<Field label="Slug">
												<input
													value={variant.slug || ""}
													onChange={(e) =>
														changeVariant(index, "slug", e.target.value)
													}
													style={{
														...inputStyle,
														fontFamily: "monospace",
													}}
												/>
											</Field>

											<Field label="Prix (CHF)" required>
												<input
													type="number"
													step="0.05"
													min="0"
													value={variant.price ?? ""}
													onChange={(e) =>
														changeVariant(index, "price", e.target.value)
													}
													style={inputStyle}
													required
												/>
											</Field>

											<Field label="Stock">
												<input
													type="number"
													min="0"
													value={variant.stock ?? ""}
													onChange={(e) =>
														changeVariant(index, "stock", e.target.value)
													}
													style={inputStyle}
												/>
											</Field>
										</div>

										<div
											style={{
												marginTop: 12,
											}}
										>
											<Field label="Description">
												<textarea
													value={variant.description || ""}
													onChange={(e) =>
														changeVariant(index, "description", e.target.value)
													}
													rows={2}
													style={{
														...inputStyle,
														resize: "vertical",
													}}
												/>
											</Field>
										</div>

										<label
											style={{
												display: "flex",
												alignItems: "center",
												gap: 8,
												marginTop: 12,
												fontSize: 13,
											}}
										>
											<input
												type="checkbox"
												checked={variant.available ?? true}
												onChange={(e) =>
													changeVariant(index, "available", e.target.checked)
												}
											/>
											Variante active
										</label>

										{/* Images existantes */}
										{variant.images?.length > 0 && (
											<div
												style={{
													display: "flex",
													flexWrap: "wrap",
													gap: 8,
													marginTop: 14,
												}}
											>
												{variant.images.map((image) => (
													<div
														key={image.id}
														style={{
															position: "relative",
														}}
													>
														<img
															src={image.image_url}
															alt=""
															style={{
																width: 80,
																height: 80,
																objectFit: "cover",
																borderRadius: 8,
																border: "1px solid #E6E1D6",
															}}
														/>

														<button
															type="button"
															onClick={() =>
																removeExistingImage(image.id, variant.id)
															}
															style={{
																position: "absolute",
																top: 4,
																right: 4,
																width: 22,
																height: 22,
																border: "none",
																borderRadius: "50%",
																background: "rgba(0,0,0,0.7)",
																color: "#fff",
																cursor: "pointer",
															}}
														>
															×
														</button>
													</div>
												))}
											</div>
										)}

										{pendingImages.length > 0 && (
											<div
												style={{
													marginTop: 12,
													padding: "8px 10px",
													borderRadius: 8,
													background: "#EAF3DE",
													color: "#3B6D11",
													fontSize: 12,
												}}
											>
												{pendingImages.length} nouvelle
												{pendingImages.length > 1 ? "s" : ""} image
												{pendingImages.length > 1 ? "s" : ""} à envoyer.
											</div>
										)}

										<div
											style={{
												marginTop: 12,
											}}
										>
											<input
												type="file"
												accept="image/*"
												multiple
												onChange={(e) => onVariantImagesChange(variantKey, e)}
											/>
										</div>
									</div>
								);
							})}
						</div>
					)}
				</section>

				{/* Footer */}
				<div
					style={{
						display: "flex",
						justifyContent: "flex-end",
						gap: 10,
						paddingTop: 16,
						borderTop: "1px solid #E6E1D6",
					}}
				>
					<button
						type="button"
						onClick={onClose}
						disabled={submitting}
						style={{
							padding: "9px 20px",
							borderRadius: 10,
							border: "0.5px solid #D3D1C7",
							background: "transparent",
							color: "#6B6B6B",
							cursor: "pointer",
						}}
					>
						Annuler
					</button>

					<button
						type="submit"
						disabled={submitting}
						style={{
							padding: "9px 24px",
							borderRadius: 10,
							border: "none",
							background: "#2A2A2A",
							color: "#FFFCF8",
							fontWeight: 600,
							cursor: submitting ? "not-allowed" : "pointer",
							opacity: submitting ? 0.6 : 1,
						}}
					>
						{submitting ? "Enregistrement..." : "Enregistrer"}
					</button>
				</div>
			</form>
		</div>
	</div>
);

// ── Main page ─────────────────────────────────────────────────────
const AdminProductsPage = () => {
	const navigate = useNavigate();

	const [products, setProducts] = useState([]);
	const [categories, setCategories] = useState([]);

	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [deleting, setDeleting] = useState(null);

	const [editOpen, setEditOpen] = useState(false);
	const [form, setForm] = useState(null);

	const [editId, setEditId] = useState(null);
	const [editLoading, setEditLoading] = useState(false);
	const [newProductImages, setNewProductImages] = useState([]);
	const [newVariantImages, setNewVariantImages] = useState({});

	// ── Load products and categories ───────────────────────────────
	useEffect(() => {
		load();
	}, []);

	const load = async () => {
		try {
			setLoading(true);

			const [productsData, categoriesData] = await Promise.all([
				getAdminProducts(),
				getCategories(),
			]);

			setProducts(productsData || []);
			setCategories(categoriesData || []);
		} catch (error) {
			console.error(
				"Erreur chargement produits admin:",
				error?.message || error,
			);
		} finally {
			setLoading(false);
		}
	};

	// ── Form change ────────────────────────────────────────────────
	const onChange = (e) => {
		const { name, value, type, checked } = e.target;

		setForm((prev) => ({
			...prev,
			[name]: type === "checkbox" ? checked : value,
		}));
	};

	// ── Image selection ────────────────────────────────────────────
	const onProductImagesChange = (e) => {
		const files = Array.from(e.target.files || []);

		const validFiles = files.filter((file) => {
			if (file.size > 5 * 1024 * 1024) {
				alert(`${file.name} dépasse 5 Mo.`);
				return false;
			}

			return true;
		});

		setNewProductImages((current) => [...current, ...validFiles]);

		e.target.value = "";
	};

	const onVariantImagesChange = (variantKey, e) => {
		const files = Array.from(e.target.files || []);

		const validFiles = files.filter((file) => {
			if (file.size > 5 * 1024 * 1024) {
				alert(`${file.name} dépasse 5 Mo.`);
				return false;
			}

			return true;
		});

		setNewVariantImages((current) => ({
			...current,
			[variantKey]: [...(current[variantKey] || []), ...validFiles],
		}));

		e.target.value = "";
	};

	const changeVariant = (index, field, value) => {
		setForm((current) => ({
			...current,
			variants: current.variants.map((variant, variantIndex) =>
				variantIndex === index
					? {
							...variant,
							[field]: value,
						}
					: variant,
			),
		}));
	};

	const addVariant = () => {
		const tempId = `new-${crypto.randomUUID()}`;

		setForm((current) => ({
			...current,
			has_variants: true,
			variants: [
				...(current.variants || []),
				{
					_tempId: tempId,
					id: null,
					name: "",
					slug: "",
					description: "",
					price: current.price || "",
					stock: 0,
					available: true,
					sort_order: current.variants?.length || 0,
					images: [],
				},
			],
		}));
	};

	const removeVariant = async (index) => {
		const variant = form.variants[index];

		if (!confirm(`Supprimer la variante "${variant.name || "Sans nom"}" ?`)) {
			return;
		}

		if (variant.id) {
			await deleteProductVariant(variant.id);
		}

		setForm((current) => {
			const variants = current.variants.filter(
				(_, variantIndex) => variantIndex !== index,
			);

			return {
				...current,
				variants,
				has_variants: variants.length > 0,
			};
		});
	};

	const removeExistingImage = async (imageId, variantId = null) => {
		if (!confirm("Supprimer cette image ?")) {
			return;
		}

		await deleteProductImage(imageId);

		setForm((current) => {
			if (!variantId) {
				return {
					...current,
					images: current.images.filter((image) => image.id !== imageId),
				};
			}

			return {
				...current,
				variants: current.variants.map((variant) =>
					variant.id === variantId
						? {
								...variant,
								images: variant.images.filter((image) => image.id !== imageId),
							}
						: variant,
				),
			};
		});
	};

	// ── Save existing product ──────────────────────────────────────
	const save = async (e) => {
		e.preventDefault();

		if (!editId || !form) return;

		setSaving(true);

		try {
			// 1. Produit principal
			// 1. Produit principal
			const variants = form.variants || [];

			await updateProductById(editId, {
				name: form.name.trim(),
				slug: form.slug.trim(),
				category_id: form.category_id || null,
				short_description: form.short_description?.trim() || null,
				description: form.description?.trim() || null,
				price: Number(form.price || 0),
				stock:
					form.stock === "" || form.stock === null ? null : Number(form.stock),
				available: Boolean(form.available),
				featured: Boolean(form.featured),
				has_variants: variants.length > 0,
			});

			// 2. Nouvelles images générales
			if (newProductImages.length > 0) {
				await addProductImages(editId, newProductImages);
			}

			// 3. Variantes
			for (let index = 0; index < variants.length; index++) {
				const variant = variants[index];

				const variantPayload = {
					name: variant.name.trim(),
					slug: variant.slug?.trim() || undefined,
					description: variant.description?.trim() || null,
					price: Number(variant.price || 0),
					stock: Number(variant.stock ?? 0),
					available: Boolean(variant.available),
					sort_order: index,
				};

				let savedVariant = variant;

				if (variant.id) {
					savedVariant = await updateProductVariant(variant.id, variantPayload);
				} else {
					savedVariant = await createProductVariant(editId, variantPayload);
				}

				const variantKey = variant.id || variant._tempId;

				const files = newVariantImages[variantKey] || [];

				if (files.length > 0) {
					await addVariantImages(editId, savedVariant.id, files);
				}
			}

			await load();

			sessionStorage.removeItem("menu_products");
			sessionStorage.removeItem("menu_products_at");
			sessionStorage.removeItem("featured_products");
			sessionStorage.removeItem("featured_products_at");

			close();
		} catch (error) {
			console.error("Erreur modification produit:", error?.message || error);

			alert(
				error?.message ||
					"Une erreur est survenue lors de la modification du produit.",
			);
		} finally {
			setSaving(false);
		}
	};

	// ── Delete product ─────────────────────────────────────────────
	const remove = async (id) => {
		if (!confirm("Supprimer ce produit définitivement ?")) {
			return;
		}

		setDeleting(id);

		try {
			await deleteProductById(id);

			setProducts((current) => current.filter((product) => product.id !== id));

			// Invalider le cache du catalogue.
			sessionStorage.removeItem("menu_products");
			sessionStorage.removeItem("menu_products_at");
			sessionStorage.removeItem("featured_products");
			sessionStorage.removeItem("featured_products_at");
		} catch (error) {
			console.error("Erreur suppression produit:", error?.message || error);

			alert(
				error?.message ||
					"Une erreur est survenue lors de la suppression du produit.",
			);
		} finally {
			setDeleting(null);
		}
	};

	// ── Open edit modal ────────────────────────────────────────────
	const openEdit = async (product) => {
		try {
			setEditLoading(true);
			setEditId(product.id);

			const fullProduct = await getAdminProductById(product.id);

			setForm({
				name: fullProduct.name || "",
				slug: fullProduct.slug || "",
				category_id: fullProduct.category_id || "",
				short_description: fullProduct.short_description || "",
				description: fullProduct.description || "",
				price: fullProduct.price ?? "",
				stock: fullProduct.stock ?? "",
				available: fullProduct.available ?? true,
				featured: fullProduct.featured ?? false,
				has_variants: fullProduct.has_variants ?? false,
				images: fullProduct.images || [],
				variants: fullProduct.variants || [],
			});

			setNewProductImages([]);
			setNewVariantImages({});
			setEditOpen(true);
		} catch (error) {
			console.error("Erreur chargement produit:", error?.message || error);

			alert(error?.message || "Impossible de charger le produit.");

			setEditId(null);
		} finally {
			setEditLoading(false);
		}
	};

	// ── Close edit modal ───────────────────────────────────────────
	const close = () => {
		setEditOpen(false);
		setEditId(null);
		setForm(null);
		setNewProductImages([]);
		setNewVariantImages({});
	};

	// ── Format price ───────────────────────────────────────────────
	const formatPrice = (price) =>
		new Intl.NumberFormat("fr-CH", {
			style: "currency",
			currency: "CHF",
		}).format(Number(price || 0));

	return (
		<div
			style={{
				maxWidth: 1280,
				margin: "0 auto",
				padding: "128px 24px 48px",
			}}
		>
			{/* Header */}
			<div
				style={{
					display: "flex",
					alignItems: "flex-start",
					justifyContent: "space-between",
					marginBottom: 32,
					gap: 20,
				}}
			>
				<div>
					<h1
						style={{
							margin: 0,
							fontSize: 28,
							fontWeight: 700,
							color: "#2A2A2A",
						}}
					>
						Produits
					</h1>

					<p
						style={{
							margin: "6px 0 0",
							fontSize: 15,
							color: "#8A857D",
						}}
					>
						{products.length} produit
						{products.length !== 1 ? "s" : ""} dans le catalogue
					</p>
				</div>

				{/* Navigation vers la page de création */}
				<button
					type="button"
					onClick={() => navigate("/admin/products/new")}
					style={{
						display: "inline-flex",
						alignItems: "center",
						gap: 8,
						padding: "10px 20px",
						background: "#2A2A2A",
						color: "#FFFCF8",
						border: "none",
						borderRadius: 12,
						fontSize: 14,
						fontWeight: 600,
						cursor: "pointer",
						fontFamily: "inherit",
						flexShrink: 0,
					}}
				>
					<span
						style={{
							fontSize: 18,
							lineHeight: 1,
						}}
					>
						+
					</span>
					Ajouter un produit
				</button>
			</div>

			{/* Loading */}
			{loading ? (
				<div
					style={{
						padding: "48px 0",
						textAlign: "center",
						color: "#8A857D",
						fontSize: 15,
					}}
				>
					Chargement des produits...
				</div>
			) : products.length === 0 ? (
				/* Empty state */
				<div
					style={{
						padding: "64px 24px",
						textAlign: "center",
						background: "#FFFCF8",
						border: "0.5px solid #E6E1D6",
						borderRadius: 16,
						color: "#8A857D",
					}}
				>
					<p
						style={{
							fontSize: 32,
							margin: "0 0 12px",
						}}
					>
						☕
					</p>

					<p
						style={{
							margin: 0,
							fontSize: 16,
							fontWeight: 500,
							color: "#2A2A2A",
						}}
					>
						Aucun produit
					</p>

					<p
						style={{
							margin: "6px 0 0",
							fontSize: 14,
						}}
					>
						Commencez par ajouter votre premier produit.
					</p>
				</div>
			) : (
				/* Products table */
				<div
					style={{
						background: "#FFFCF8",
						border: "0.5px solid #E6E1D6",
						borderRadius: 16,
						overflow: "hidden",
					}}
				>
					<div
						style={{
							overflowX: "auto",
						}}
					>
						<table
							style={{
								width: "100%",
								minWidth: 850,
								borderCollapse: "collapse",
								tableLayout: "fixed",
							}}
						>
							<colgroup>
								<col style={{ width: 110 }} />
								<col style={{ width: "28%" }} />
								<col style={{ width: "16%" }} />
								<col style={{ width: "11%" }} />
								<col style={{ width: "9%" }} />
								<col style={{ width: "14%" }} />
								<col style={{ width: 100 }} />
							</colgroup>

							<thead>
								<tr
									style={{
										background: "#F5F3EE",
										borderBottom: "0.5px solid #E6E1D6",
									}}
								>
									{[
										"",
										"Produit",
										"Catégorie",
										"Prix",
										"Stock",
										"Statut",
										"",
									].map((heading, index) => (
										<th
											key={index}
											style={{
												padding: "11px 14px",
												textAlign: "left",
												fontSize: 11,
												fontWeight: 600,
												color: "#8A857D",
												letterSpacing: "0.1em",
												textTransform: "uppercase",
											}}
										>
											{heading}
										</th>
									))}
								</tr>
							</thead>

							<tbody>
								{products.map((product, index) => (
									<tr
										key={product.id}
										style={{
											borderBottom:
												index < products.length - 1
													? "0.5px solid #F1ECE4"
													: "none",
											transition: "background 0.15s",
										}}
										onMouseEnter={(e) =>
											(e.currentTarget.style.background = "#FAF8F4")
										}
										onMouseLeave={(e) =>
											(e.currentTarget.style.background = "transparent")
										}
									>
										{/* Image */}
										<td
											style={{
												padding: "14px 14px 14px 20px",
												verticalAlign: "middle",
											}}
										>
											<ProductThumb
												src={product.image_url}
												alt={product.name}
											/>
										</td>

										{/* Product */}
										<td
											style={{
												padding: "12px 14px",
											}}
										>
											<p
												style={{
													margin: 0,
													fontWeight: 600,
													fontSize: 14,
													color: "#2A2A2A",
												}}
											>
												{product.name}
											</p>

											<p
												style={{
													margin: "2px 0 0",
													fontSize: 12,
													color: "#B4B2A9",
													fontFamily: "monospace",
												}}
											>
												{product.slug}
											</p>
										</td>

										{/* Category */}
										<td
											style={{
												padding: "12px 14px",
											}}
										>
											<span
												style={{
													display: "inline-block",
													padding: "3px 10px",
													borderRadius: 20,
													fontSize: 12,
													fontWeight: 500,
													background: "#F1ECE4",
													color: "#6B6B6B",
												}}
											>
												{product.categories?.name || "—"}
											</span>
										</td>

										{/* Price */}
										<td
											style={{
												padding: "12px 14px",
												fontSize: 14,
												fontWeight: 600,
												color: "#2A2A2A",
											}}
										>
											{formatPrice(product.price)}
										</td>

										{/* Stock */}
										<td
											style={{
												padding: "12px 14px",
												fontSize: 14,
												color: product.stock === 0 ? "#E24B4A" : "#2A2A2A",
											}}
										>
											{product.stock ?? "—"}
										</td>

										{/* Status */}
										<td
											style={{
												padding: "12px 14px",
											}}
										>
											<StatusBadge
												available={product.available}
												stock={product.stock}
											/>
										</td>

										{/* Actions */}
										<td
											style={{
												padding: "12px 14px",
											}}
										>
											<div
												style={{
													display: "flex",
													gap: 6,
												}}
											>
												<button
													type="button"
													onClick={() => openEdit(product)}
													title="Modifier"
													style={{
														padding: "6px 10px",
														borderRadius: 8,
														border: "0.5px solid #D3D1C7",
														background: "transparent",
														color: "#6B6B6B",
														cursor: "pointer",
														fontSize: 13,
														fontFamily: "inherit",
													}}
												>
													✏️
												</button>

												<button
													type="button"
													onClick={() => remove(product.id)}
													title="Supprimer"
													disabled={deleting === product.id}
													style={{
														padding: "6px 10px",
														borderRadius: 8,
														border: "0.5px solid #F7C1C1",
														background: "transparent",
														color: "#A32D2D",
														cursor:
															deleting === product.id
																? "not-allowed"
																: "pointer",
														fontSize: 13,
														opacity: deleting === product.id ? 0.5 : 1,
														fontFamily: "inherit",
													}}
												>
													{deleting === product.id ? "..." : "🗑"}
												</button>
											</div>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				</div>
			)}

			{/* Edit modal */}
			{editOpen && form && (
				<ProductEditModal
					form={form}
					onChange={onChange}
					onSubmit={save}
					onClose={close}
					submitting={saving}
					categories={categories}
					newProductImages={newProductImages}
					newVariantImages={newVariantImages}
					onProductImagesChange={onProductImagesChange}
					onVariantImagesChange={onVariantImagesChange}
					changeVariant={changeVariant}
					addVariant={addVariant}
					removeVariant={removeVariant}
					removeExistingImage={removeExistingImage}
				/>
			)}
		</div>
	);
};

export default AdminProductsPage;
